<!-- Displays a card showing the journey details -->
<script setup lang="ts">
import { handleError } from "@/services/AlertService";
import type { Ref } from "vue";
import { onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { DateTime } from "luxon";
import { JourneyViewType } from "@/models/basket/JourneyViewType";
import TicketDetails from "@/components/3-Basket/TicketDetails.vue";
import { getFareForFareKey } from "@/utilities/FareFromKey";
import { getTicketDetails, getRestrictionDetails } from "@/utilities/GetTicketDetails";
import type { DeliveryMethods } from "@/models/basket/DeliveryMethods";
import { useBookingData } from "@/stores/booking-storage";
import type { FaresByKeyModel, TicketRestrictionSummary, TicketDetailsByKey } from "@/models/JourneySelectorViewModel";
import type { IGQLBookingOverview, IGQLTripsOverview, ITicketKey } from "@/models/graphql/BookingOverviewResponse";
import { formatPassengers } from "@/utilities/PassengerFormatter";
import { getBooking } from "@/graphql/functions/getBooking";
import { deleteTripFromBooking } from "@/graphql/functions/deleteTripFromBooking";
import { getUniqueListOfTickets } from "@/utilities/getUniqueListOfTickets";
import { getFormattedDurationString } from "@/utilities/getFormattedDurationString";
import { getTimeFromDateTimeIso } from "@/utilities/getTimeFromDateTimeIso";

const isLoading: Ref<boolean> = ref(false);
const route = useRoute();
// This ref holds the detailed booking overview fetched for this page
const localBookingData: Ref<IGQLBookingOverview | null> = ref(null);
const journeyTypeSelection: Ref<JourneyViewType> = ref(JourneyViewType.Outbound);
const deliveryMethod: Ref<DeliveryMethods | string> = ref("");
const ticketDetails: Ref<TicketDetailsByKey | null> = ref(null);
const fareDetails: Ref<FaresByKeyModel | null> = ref(null);
const td: Ref = ref(null); // Ref for TicketDetails component instance
const ticketRestrictions: Ref<TicketRestrictionSummary | null> = ref(null);
const bookingStore = useBookingData();

async function getBookingDetails() {
    isLoading.value = true;
    // Get booking ID from the store (which checks expiration)
    const bookingID = bookingStore.checkExistingBooking();

    if (!bookingID) {
        console.warn("No valid booking ID found in store or booking expired.");
        handleError("Your booking could not be found or has expired. Please start again.");
        // Clear local state
        localBookingData.value = null;
        isLoading.value = false;
        return;
    }

    try {
        const overviewResponse = await getBooking(bookingID);
        // Update the local ref for this component's display
        localBookingData.value = overviewResponse;
        // ALSO update the Pinia store's bookingData to keep it synchronized
        bookingStore.bookingData = overviewResponse;
        // Determine delivery method (ensure route query exists)
        deliveryMethod.value = (route.query.deliveryMethod as string) || "";
        isLoading.value = false;
    } catch (error) {
        isLoading.value = false;
        handleError(error);
        // Clear data on error
        localBookingData.value = null;
        // Clear store data too
        bookingStore.clearBookingData();
    }
}

// Return just the date in a specific format for display (e.g., "Wed, 1 March 14:02")
function formatDate(date: string | undefined | null): string {
    // Added null check
    return date ? DateTime.fromISO(date).toFormat("EEE, d MMMM HH:mm") : "Date unavailable";
}

function toggleTicketDetail(selectedTicketTicket: ITicketKey, item: IGQLTripsOverview, journeyType: JourneyViewType) {
    const { fareKey } = selectedTicketTicket;
    if (!fareKey) {
        handleError("Ticket details could not be loaded (missing identifier).");
        return;
    }

    // Use the localBookingData ref which holds the detailed overview for this page
    if (!localBookingData.value) {
        handleError("Booking details are not available.");
        return;
    }

    const fareDetailsKey = getFareForFareKey(fareKey, item.fares);
    if (!fareDetailsKey) {
        handleError("Could not find fare details for the selected ticket.");
        return;
    }

    // Pass the value of the local ref to utility functions
    const ticketDetailResult = getTicketDetails(localBookingData.value, fareDetailsKey);
    if (!ticketDetailResult || !ticketDetailResult.value) {
        handleError("Could not extract ticket details.");
        return;
    }

    journeyTypeSelection.value = journeyType;
    fareDetails.value = fareDetailsKey;
    ticketDetails.value = ticketDetailResult.value; // Assign the nested value

    const { restrictionCode } = fareDetails.value;
    ticketRestrictions.value = null; // Reset restrictions first
    if (restrictionCode && restrictionCode !== "") {
        try {
            ticketRestrictions.value = getRestrictionDetails(localBookingData.value, restrictionCode);
        } catch (e) {
            console.error("Error getting restriction details:", e);
            // Optionally inform user restriction details are unavailable
        }
    }

    // Ensure the TicketDetails component instance (td) is available
    if (td.value && typeof td.value.showDialog === "function") {
        td.value.showDialog();
    } else {
        console.error("TicketDetails component reference or showDialog method not found.");
        handleError("Could not display ticket details dialog.");
    }
}

async function deleteBooking(tripId: string) {
    // Get booking ID from the currently displayed local data
    const bookingID = localBookingData.value?.id;
    if (!bookingID) {
        handleError("Cannot delete trip: Booking ID is missing.");
        return;
    }

    // Optional: Add confirmation dialog here
    // if (!confirm("Are you sure you want to delete this trip?")) return;

    isLoading.value = true;
    try {
        // Call the delete mutation
        await deleteTripFromBooking(bookingID, tripId);
        // Successfully deleted, now refresh the booking details
        // getBookingDetails() will handle setting isLoading=false on completion/error
        await getBookingDetails();
        handleError("Trip successfully deleted.");
    } catch (error) {
        // isLoading is handled by getBookingDetails in success case, need to handle here on error
        isLoading.value = false;
        handleError(error); // Centralized error handling
    }
}

onMounted(() => {
    // Fetch the booking details when the component mounts
    getBookingDetails();
});
</script>

<template>
    <div>
        <v-overlay v-model="isLoading" contained class="align-center justify-center" persistent>
            <v-progress-circular indeterminate :size="70" :width="7" color="primary"> </v-progress-circular>
        </v-overlay>

        <v-container class="pa-0 pa-md-2 pb-5">
            <!-- Loop through trips in the locally fetched booking data -->
            <div class="checkout-ticket-card pb-5" v-for="(item, i) in localBookingData?.trips" :key="item.id || i">
                <v-card>
                    <v-card-title class="ticket-head mt-2">
                        <div class="delivery-method" v-if="deliveryMethod === 'TOD'">
                            <font-awesome-icon icon="fa-solid fa-ticket" class="fa-lg pr-3"></font-awesome-icon>
                            <!-- Updated Icon -->
                            Ticket on Departure
                        </div>
                        <div class="delivery-method" v-else-if="deliveryMethod === 'E_TICKET'">
                            <!-- Check specific value -->
                            <font-awesome-icon icon="fa-solid fa-qrcode" class="fa-lg pr-3"></font-awesome-icon>
                            eTicket
                        </div>
                        <div class="delivery-method" v-else>
                            <!-- Default or unknown delivery method -->
                            Delivery: {{ deliveryMethod || "Not specified" }}
                        </div>
                        <!-- Show delete button only if there's more than one trip -->
                        <!--<button class="pr-3 pl-3 delete-button" v-if="localBookingData && localBookingData.trips.length > 1" @click="deleteBooking(item.id)" :disabled="isLoading">
                            <font-awesome-icon icon="fa-solid fa-trash" class="pr-2"></font-awesome-icon>Delete
                        </button>-->
                    </v-card-title>
                    <div class="ticket-type pl-3 pr-3 pb-1 pt-1">
                        <!-- Determine ticket type -->
                        <span v-if="item.inboundJourney || item.isOpenReturn">Return</span>
                        <span v-else>Single</span>
                    </div>
                    <v-card-text>
                        <div class="journey-summary pb-3">
                            <b>{{ item.outboundJourney.originName }}</b> to
                            <b>{{ item.outboundJourney.destinationName }}</b>
                        </div>
                        <!-- Outbound Journey Details -->
                        <div class="pb-5 journey">
                            <div class="pb-2"><span class="pr-2 journey-leg-label">OUT</span> {{ formatDate(item.outboundJourney.departing) }}</div>
                            <div class="time">
                                <div class="pr-3">{{ getTimeFromDateTimeIso(item.outboundJourney.departing) }}</div>
                                <font-awesome-icon icon="fa-solid fa-arrow-right"></font-awesome-icon>
                                <div class="pl-3">{{ getTimeFromDateTimeIso(item.outboundJourney.arriving) }}</div>
                            </div>
                            <div class="duration pt-2">{{ item.outboundJourney.changes }} change{{ item.outboundJourney.changes !== 1 ? "s" : "" }}, {{ getFormattedDurationString(item.outboundJourney.totalTimeIso) }}</div>
                            <!-- Inbound Journey Details (if exists) -->
                            <div v-if="item.inboundJourney" class="pt-5">
                                <div class="pb-2"><span class="pr-2 journey-leg-label">RETURN</span>{{ formatDate(item.inboundJourney.departing) }}</div>
                                <div class="time">
                                    <div class="pr-3">{{ getTimeFromDateTimeIso(item.inboundJourney.departing) }}</div>
                                    <font-awesome-icon icon="fa-solid fa-arrow-right"></font-awesome-icon>
                                    <div class="pl-3">{{ getTimeFromDateTimeIso(item.inboundJourney.arriving) }}</div>
                                </div>
                                <div class="duration pt-2">{{ item.inboundJourney.changes }} change{{ item.inboundJourney.changes !== 1 ? "s" : "" }}, {{ getFormattedDurationString(item.inboundJourney.totalTimeIso) }}</div>
                            </div>
                        </div>
                        <v-divider></v-divider>
                        <!-- Ticket Links -->
                        <div class="pt-3 tickets-section">
                            <div v-if="item.outboundTickets && item.outboundTickets.length > 0">
                                <b>Out: </b>
                                <span v-for="(ticket, j) in getUniqueListOfTickets(item.outboundTickets)" :key="`out-${j}`" class="ticket-link-wrapper">
                                    <a href="#" @click.prevent="toggleTicketDetail(ticket, item, JourneyViewType.Outbound)">{{ getFareForFareKey(ticket.fareKey, item.fares)?.displayName || "View Details" }}</a>
                                </span>
                            </div>
                            <div v-if="item.inboundTickets && item.inboundTickets.length > 0">
                                <b>Return: </b>
                                <span v-for="(ticket, j) in getUniqueListOfTickets(item.inboundTickets)" :key="`in-${j}`" class="ticket-link-wrapper">
                                    <a href="#" @click.prevent="toggleTicketDetail(ticket, item, JourneyViewType.Inbound)">{{ getFareForFareKey(ticket.fareKey, item.fares)?.displayName || "View Details" }}</a>
                                </span>
                            </div>
                        </div>
                        <!-- Passenger Details -->
                        <div class="passengers pt-3 pb-3">
                            <font-awesome-icon icon="fa-solid fa-user-friends" class="mr-2"></font-awesome-icon>
                            <span>{{ formatPassengers(item.adults, false) }}</span>
                            <span v-if="item.children > 0">, {{ formatPassengers(item.children, true) }}</span>
                        </div>
                        <v-divider></v-divider>
                        <!-- Journey Price -->
                        <div class="pt-5 price">
                            Journey total
                            <span
                                ><b>{{ item.totalPriceFormatted || "N/A" }}</b></span
                            >
                        </div>
                    </v-card-text>
                </v-card>
            </div>

            <!-- Overall Booking Summary Card -->
            <v-card class="pa-5 mt-5 booking-summary-card" v-if="localBookingData">
                <div class="price">
                    Total fare(s)
                    <span
                        ><b>{{ localBookingData.bookingAmountFormatted }}</b></span
                    >
                </div>
                <div class="price">
                    Booking fee
                    <span
                        ><b>{{ localBookingData.bookingFeeFormatted || "N/A" }}</b></span
                    >
                </div>
                <!-- Conditionally show On Account Fee based on STORE state -->
                <div class="price" v-if="bookingStore.isOnAccountPaymentSelected()">
                    On account statement fee
                    <span
                        ><b>{{ localBookingData.onAccountBookingFeeFormatted }}</b></span
                    >
                </div>
                <div class="pt-5 price total-amount">
                    Amount to pay
                    <span v-if="bookingStore.isOnAccountPaymentSelected()"
                        ><b>{{ localBookingData.totalOnAccountPaymentAmountFormatted || "N/A" }}</b></span
                    >
                    <span v-else
                        ><b>{{ localBookingData.totalPaymentAmountFormatted || "N/A" }}</b></span
                    >
                </div>
            </v-card>

            <v-card class="pa-5 mt-5" v-else-if="!isLoading">
                <p>Booking details could not be loaded.</p>
            </v-card>
        </v-container>

        <!-- Ticket Details Dialog -->
        <TicketDetails ref="td" :ticket-detail="ticketDetails" :journey-fare="fareDetails" :fare-type="journeyTypeSelection" :restrictionDetails="ticketRestrictions"></TicketDetails>
    </div>
</template>

<style scoped lang="scss">
.checkout-ticket-card {
    width: 100%;
}

.ticket-type {
    position: relative;
    width: fit-content;
    background-color: var(--vt-c-orange);
    color: white;
    padding: 4px 8px;
    border-radius: 0px 5px 5px 0px;
    font-size: 0.9rem;
    font-weight: bold;
}

.journey-summary {
    font-size: 1.2rem;
    line-height: 1.4;
    font-weight: 500;
}

.v-card {
    border: 1px solid #e0e0e0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border-radius: 8px;

    .v-card-text {
        line-height: 1.4rem;
        color: #333;
    }

    .v-card-title.ticket-head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 16px;
        font-size: 1rem;
        border-bottom: 1px solid #eee;
    }
}

.delivery-method {
    display: flex;
    align-items: center;
    font-weight: 500;
}

.delete-button {
    background-color: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 5px;
    padding: 4px 12px !important;
    font-size: 0.9rem;
    cursor: pointer;
    transition: background-color 0.2s;
    color: #d32f2f;

    &:hover:not(:disabled) {
        background-color: #efefef;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .fa-trash {
        margin-right: 4px;
    }
}

.journey {
    .journey-leg-label {
        display: inline-block;
        background-color: #eee;
        padding: 2px 6px;
        border-radius: 4px;
        font-size: 0.8rem;
        font-weight: bold;
        margin-right: 4px;
    }
}

.time {
    display: flex;
    align-items: center;
    font-size: 1.2rem;

    div {
        font-weight: 700;
    }

    .fa-arrow-right {
        margin: 0 8px;
        color: #777;
    }
}

.duration {
    font-style: italic;
    color: #555;
    font-size: 0.9rem;
}

.tickets-section {
    font-size: 0.95rem;

    b {
        // Style the "Out:" and "Return:" labels
        margin-right: 4px;
    }

    .ticket-link-wrapper {
        margin-right: 8px;

        &:last-child {
            margin-right: 0;
        }

        a {
            color: var(--vt-c-blue);
            text-decoration: underline;

            &:hover {
                text-decoration: none;
            }
        }
    }
}

.passengers {
    color: #333;
    font-size: 0.95rem;
    display: flex;
    align-items: center;

    .fa-user-friends {
        color: #555;
    }
}

.price {
    font-size: 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;

    span {
        font-weight: bold;
    }
}

.total-amount {
    font-size: 1.1rem;
    font-weight: bold;
    color: #000;
    border-top: 1px solid #eee;
    padding-top: 10px;
    margin-top: 10px;
}

.booking-summary-card {
    border: 1px solid #ccc;
}

@media only screen and (max-width: 760px) {
    .checkout-ticket-card {
        width: 100%;
    }

    .v-card-title.ticket-head {
        flex-direction: column;
        align-items: flex-start;
    }

    .delete-button {
        margin-top: 8px;
        align-self: flex-end;
    }

    .time {
        font-size: 1.1rem;
    }

    .price {
        font-size: 0.95rem;
    }

    .total-amount {
        font-size: 1.05rem;
    }
}
</style>
